import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DefaultLayout from 'app/layouts/DefaultLayout';
import MainContentLayout from 'app/layouts/MainContentLayout';
import Path from 'app/route/Path';
import { toast } from 'react-toastify';
import GA, { EVENT_NAMES } from 'app/services/googleAnalytics';
import BasicInfoSection from './components/BasicInfoSection';
import DetailedSettingsSection from './components/DetailedSettingsSection';
import CombinationSection from './components/CombinationSection';
import NotesSection from './components/NotesSection';
import PublicSettingsSection from './components/PublicSettingsSection';
import { createMenu } from 'app/services/api';
import NavigationBackBtn from '../../../components/common/NavigationBackButton';

function CreateMenuPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [customTags, setCustomTags] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (values) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const menuData = {
        name: values.name,
        description: values.description,
        category: values.category,
        treatmentTime: parseInt(values.treatmentTime, 10),
        managementTags: values.managementTags,
        targetAudiences: values.targetAudiences,
        recommendedMenus: values.recommendedMenus,
        avoidSimultaneousMenus: values.avoidSimultaneousMenus,
        avoidConditions: values.avoidConditions,
        customerNotes: values.customerNotes,
        publicStatus: values.publicStatus,
        showOnBookingSite: values.showOnBookingSite,
        isDraft: false,
      };

      const res = await createMenu(menuData);
      if (res.success) {
        toast.success('メニューを登録しました');
        GA.tracking(EVENT_NAMES.CREATE_MENU?.CREATE);
        navigate(Path.menuManagement);
      } else {
        toast.error(location.state?.copyData ? 'メニューのコピーに失敗しました' : 'メニューの作成に失敗しました');
      }
    } catch (error) {
      console.error('Create menu error:', error);
      toast.error(location.state?.copyData ? 'メニューのコピーに失敗しました' : 'メニューの作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('メニュー名は必須です'),
    description: Yup.string().trim().required('説明文は必須です'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      category: '',
      treatmentTime: '',
      managementTags: [],
      targetAudiences: [],
      recommendedMenus: [],
      avoidSimultaneousMenus: [],
      avoidConditions: [],
      customerNotes: '',
      publicStatus: true,
      showOnBookingSite: true,
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit,
  });

  const { values, errors, handleBlur, setFieldValue, handleSubmit, dirty, setValues } = formik;

  useEffect(() => {
    if (location.state?.copyData) {
      const { copyData } = location.state;
      setValues({
        name: copyData.name || '',
        description: copyData.description || '',
        category: copyData.category || '',
        treatmentTime: copyData?.treatmentTime ? copyData?.treatmentTime?.toString() : '',
        managementTags: copyData.managementTags || [],
        targetAudiences: copyData.targetAudiences || [],
        recommendedMenus: copyData.recommendedMenus || [],
        avoidSimultaneousMenus: copyData.avoidSimultaneousMenus || [],
        avoidConditions: copyData.avoidConditions || [],
        customerNotes: copyData.customerNotes || '',
        publicStatus: copyData.publicStatus !== false,
        showOnBookingSite: copyData.showOnBookingSite !== false,
      });

      const defaultTags = ['新規顧客限定', '平日限定', '期間限定'];
      const custom = (copyData.managementTags || []).filter((tag) => !defaultTags.includes(tag));
      setCustomTags(custom);
    }
  }, [location.state]);

  return (
    <DefaultLayout>
      <MainContentLayout containerClass="!overflow-visible">
        <div className="w-full mx-auto">
          <div className="sticky top-[38px] z-40 bg-white/95 backdrop-blur-sm flex items-center justify-between px-4 md:px-0 border-b border-slate-100 h-14 -mt-4">
            <h2 className="text-base lg:text-xl font-semibold">
              <NavigationBackBtn url={Path.menuManagement} text={'メニュー管理'} />
            </h2>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleSubmit()}
                disabled={!dirty}
                className={`bg-blue-btn-primary text-white text-xs md:text-sm font-bold px-4 py-2 rounded-lg shadow-sm ${
                  dirty ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                保存する
              </button>
            </div>
          </div>

          <div className="w-full">
            <BasicInfoSection values={values} errors={errors} handleBlur={handleBlur} setFieldValue={setFieldValue} />

            <DetailedSettingsSection
              values={values}
              setFieldValue={setFieldValue}
              customTags={customTags}
              setCustomTags={setCustomTags}
            />

            <CombinationSection values={values} setFieldValue={setFieldValue} />

            <NotesSection values={values} setFieldValue={setFieldValue} />

            <PublicSettingsSection values={values} setFieldValue={setFieldValue} />
          </div>
        </div>
      </MainContentLayout>
    </DefaultLayout>
  );
}

export default CreateMenuPage;
